import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiCall } from '../../utils/fetchAPI';
import { useCityReference } from '../city/useCityReference';
import { translateApiMessage } from '../../utils/apiMessages';

const DEFAULT_PAGE_SIZE = 20;

/**
 * The neighbourhood agenda: what is coming up, filtered by neighbourhood.
 *
 * Creating or cancelling an event re-reads the list rather than patching a row
 * in place — what the agenda holds is the server's to decide.
 */
export function useAgenda() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { city, neighborhoods, cityParam } = useCityReference();

  const filters = useMemo(
    () => ({
      neighborhood: searchParams.get('neighborhood') || '',
      q: searchParams.get('q') || '',
      past: '1' === searchParams.get('past'),
      page: Number(searchParams.get('page')) || 1,
    }),
    [searchParams]
  );

  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      const params = new URLSearchParams();
      if (cityParam) params.set('city', cityParam);
      if (filters.neighborhood) params.set('neighborhood', filters.neighborhood);
      if (filters.q) params.set('q', filters.q);
      if (filters.past) params.set('past', '1');
      params.set('page', String(filters.page));
      params.set('limit', String(DEFAULT_PAGE_SIZE));

      try {
        const response = await apiCall(`/events?${params.toString()}`, { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (cancelled) return;

        if (!response.ok) {
          throw new Error(translateApiMessage(data.error) || 'L’agenda n’a pas pu être chargé.');
        }

        setEvents(data.data || []);
        setMeta(data.meta || null);
        setError('');
      } catch (err) {
        if (!cancelled) {
          setEvents([]);
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [filters, reloadToken, cityParam]);

  const setFilter = useCallback(
    (name, value) => {
      const next = new URLSearchParams(searchParams);

      if (value) {
        next.set(name, value);
      } else {
        next.delete(name);
      }

      if ('page' !== name) {
        next.delete('page');
      }

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return {
    filters,
    events,
    meta,
    city,
    neighborhoods,
    loading,
    error,
    setFilter,
    reload,
    pageSize: DEFAULT_PAGE_SIZE,
  };
}
